import axios from 'axios';
import { toast } from 'sonner';
import { removeAuthToken } from '@/contexts/auth-context';
import reportFrontendError from '@/lib/report-frontend-error';

// Centralized unauthorized handler
const handleUnauthorized = () => {
  removeAuthToken();
  window.location.href = '/auth/login';
};

export const publicClient = axios.create({
  baseURL: `${import.meta.env.VITE_API_BASE_URL}/api`,
  headers: {
    Accept: 'application/json',
  },
});

export const protectedClient = axios.create({
  baseURL: `${import.meta.env.VITE_API_BASE_URL}/api`,
  headers: {
    Accept: 'application/json',
  },
});

// Attach the latest token dynamically
protectedClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

protectedClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      handleUnauthorized();
    }
    return Promise.reject(error);
  },
);

const NETWORK_RETRY_ATTEMPTS = 2;
const NETWORK_RETRY_DELAY_MS = 1000;

// Retries a request only when axios got no response at all (a dropped connection,
// e.g. a mobile network handoff mid-upload) - never on a real server response
// (validation errors, 5xx), so nothing already processed by the server gets resent.
export const requestWithNetworkRetry = async <T = unknown>(
  config: Parameters<typeof protectedClient.request>[0],
): Promise<T> => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      const { data } = await protectedClient.request<T>(config);
      return data;
    } catch (err) {
      const isNetworkError = axios.isAxiosError(err) && !err.response;
      if (!isNetworkError || attempt >= NETWORK_RETRY_ATTEMPTS) {
        throw err;
      }
      await new Promise((resolve) =>
        setTimeout(resolve, NETWORK_RETRY_DELAY_MS),
      );
    }
  }
};

export const postWithNetworkRetry = <T = unknown>(
  url: string,
  body: FormData,
): Promise<T> =>
  requestWithNetworkRetry<T>({ method: 'post', url, data: body });

export const handleRequestErrors = (err: unknown) => {
  console.error(err);

  if (!axios.isAxiosError(err)) {
    return;
  }

  if (err.response) {
    const errorMsg = err.response.data?.error || err.response.data?.message;
    toast.error(errorMsg || JSON.stringify(err.response.data));
    return;
  }

  toast.error(
    err.code === 'ECONNABORTED'
      ? 'The request timed out. Please check your connection and try again.'
      : 'Network error. Please check your connection and try again.',
  );

  // A resumable upload (see lib/chunked-upload.ts) attaches a trace of every
  // chunk attempt onto the error before rethrowing - forward it along so the
  // alert shows where and how the upload broke down, not just "Network Error".
  const uploadDiagnostics = (err as { uploadDiagnostics?: unknown })
    .uploadDiagnostics;

  reportFrontendError({
    message: `${err.code || 'NETWORK_ERROR'}: ${err.message} (${err.config?.method?.toUpperCase()} ${err.config?.url})`,
    details: uploadDiagnostics ? JSON.stringify(uploadDiagnostics) : undefined,
  });
};
